import * as React from "react";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { AttachmentKind, ComposerConfig } from "../../types";
import { join } from "../../utils/classNames";
import {
	ArrowUp,
	Square,
	Mic,
	ComposerExpandIcon,
	ComposerCollapseIcon,
	Paperclip,
	ImageGlyph,
} from "../icons";
import { AttachMenu } from "./components/AttachMenu";
import { AttachmentChips } from "./components/AttachmentChips";
import { ModelMenu } from "./components/ModelMenu";
import styles from "./Input.module.css";

/** Default disclaimer under the composer, overridable/hideable via the `disclaimer` prop. */
const DEFAULT_DISCLAIMER =
	"PLEXIE can make mistakes and it's bound to your account's data access. Verify Important Data.";

interface Props {
	loading?: boolean;
	placeholder?: string;
	/** When true, the input is locked (e.g. an ended session) and shows `disabledPlaceholder`. */
	disabled?: boolean;
	disabledPlaceholder?: string;
	onSubmit: (text: string) => void;
	onAbort?: () => void;
	composer?: ComposerConfig;
	/** Small disclaimer under the composer. `undefined` → default copy; `null` → hidden. */
	disclaimer?: string | null;
}

/** Content this many rendered lines (or more) switches the pill into the card layout. */
const CARD_LINES = 2;
/** Line-count heuristic for pill→card. These MUST mirror the textarea's CSS: PAD_V is the
 *  vertical padding (10px top + 10px bottom) and LINE_H the line-height — see `.textarea` in
 *  Input.module.css. If you change either there, update these or the morph math drifts. */
const PAD_V = 20;
const LINE_H = 20;

export const Input: React.FC<Props> = ({
	loading,
	placeholder = "Ask…",
	disabled = false,
	disabledPlaceholder = "This conversation has timed out — start a new one to keep exploring.",
	onSubmit,
	onAbort,
	composer,
	disclaimer,
}) => {
	const [value, setValue] = useState("");
	const [multiline, setMultiline] = useState(false);
	const [fullscreen, setFullscreen] = useState(false);
	const ref = useRef<HTMLTextAreaElement>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	// Mirror of `multiline` for the hysteresis check inside measure() without stale closures.
	const multilineRef = useRef(false);
	multilineRef.current = multiline;

	// Auto-size the textarea and decide pill vs. card. Hysteresis: once in card, stay
	// there until the field is cleared, so the pill↔card width change can't oscillate.
	const measure = useCallback(() => {
		const el = ref.current;
		if (!el) return;
		// A locked (ended) session always renders as the pill; reset any tall card height
		// and exit fullscreen so the collapsed input doesn't stay stretched with no way out
		// (the expand/collapse button is gone once the composer leaves the card layout).
		if (disabled) {
			el.style.height = "40px";
			if (multilineRef.current) setMultiline(false);
			if (fullscreen) setFullscreen(false);
			return;
		}
		if (fullscreen) {
			el.style.height = "";
			return;
		}
		el.style.height = "auto";
		const sh = el.scrollHeight;
		const lines = Math.max(1, Math.round((sh - PAD_V) / LINE_H));
		const nowMulti = value.trim() === "" ? false : multilineRef.current || lines >= CARD_LINES;
		if (nowMulti) {
			const max = Math.round(window.innerHeight * 0.45);
			el.style.height = Math.min(sh, max) + "px";
		} else {
			el.style.height = "40px";
		}
		if (nowMulti !== multilineRef.current) setMultiline(nowMulti);
	}, [fullscreen, value, disabled]);

	useLayoutEffect(() => {
		measure();
	}, [value, fullscreen, measure]);

	const submit = () => {
		const text = value.trim();
		if (!text || loading || disabled) return;
		onSubmit(text);
		setValue("");
		setMultiline(false);
		setFullscreen(false);
	};

	const handleButtonClick = () => {
		if (loading) onAbort?.();
		else submit();
	};

	const attachments = composer?.attachments ?? [];
	const hasAttachments = attachments.length > 0;

	const canAbort = loading && !!onAbort;
	const hasText = value.trim().length > 0;
	const showSend = hasText || loading;
	const sendDisabled = disabled ? true : loading ? !canAbort : !hasText;
	// Attachments need the roomier card layout for their chip row.
	const isCard = (multiline || fullscreen || hasAttachments) && !disabled;
	const showControls = !disabled;
	// `undefined` → default copy; `null` → hidden.
	const disclaimerText = disclaimer === undefined ? DEFAULT_DISCLAIMER : disclaimer;

	const onUploadFiles = composer?.attach?.onUploadFiles;
	const pickFiles = () => fileInputRef.current?.click();

	// Available attachment actions. Exactly one → show that type's icon directly (no menu).
	const attachActions: { kind: AttachmentKind; label: string; fn: () => void }[] = [];
	if (onUploadFiles) attachActions.push({ kind: "file", label: "Upload Files", fn: pickFiles });
	if (composer?.attach?.onUploadImages)
		attachActions.push({ kind: "image", label: "Upload Images", fn: composer.attach.onUploadImages });

	const [soleAttachAction] = attachActions;
	const attachEl =
		!soleAttachAction ? null : attachActions.length === 1 ? (
			<button
				type="button"
				className={styles.iconBtn}
				onClick={soleAttachAction.fn}
				aria-label={soleAttachAction.label}
			>
				{soleAttachAction.kind === "image" ? <ImageGlyph /> : <Paperclip />}
			</button>
		) : (
			<AttachMenu attach={composer!.attach!} onPickFiles={pickFiles} />
		);

	const fileInput = onUploadFiles ? (
		<input
			ref={fileInputRef}
			type={"file"}
			multiple
			hidden
			onChange={(e) => {
				const files = Array.from(e.target.files ?? []);
				// Reset so picking the same file again still fires a change.
				e.target.value = "";
				if (files.length > 0) onUploadFiles(files);
			}}
		/>
	) : null;

	const clusterEl = (
		<div className={styles.cluster}>
			{composer?.models && <ModelMenu models={composer.models} />}
			{composer?.voice && (
				<button
					type="button"
					className={styles.iconBtn}
					onClick={composer.voice.onStart}
					aria-label="Voice input"
				>
					<Mic />
				</button>
			)}
			{showSend && (
				<button
					type="button"
					className={join(styles.sendBtn, loading && styles.stop)}
					onClick={handleButtonClick}
					disabled={sendDisabled}
					aria-label={loading ? (canAbort ? "Stop generating" : "Sending") : "Send"}
				>
					{loading ? <Square /> : <ArrowUp />}
				</button>
			)}
		</div>
	);

	const textarea = (
		<textarea
			ref={ref}
			// When empty (showing only the placeholder), keep it on one line so a narrow pill —
			// e.g. once the stop button appears while generating — can't wrap and clip it.
			// Typing restores wrapping so the pill→card morph still works.
			className={join(styles.textarea, !value && !disabled && styles.singleLine)}
			value={value}
			onChange={(e) => setValue(e.target.value)}
			onKeyDown={(e) => {
				if (e.key === "Enter" && !e.shiftKey) {
					e.preventDefault();
					submit();
				}
			}}
			placeholder={disabled ? disabledPlaceholder : placeholder}
			rows={1}
			disabled={loading || disabled}
		/>
	);

	const expandEl = isCard ? (
		<button
			type="button"
			className={join(styles.iconBtn, styles.expandBtn)}
			onClick={() => setFullscreen((f) => !f)}
			aria-label={fullscreen ? "Collapse composer" : "Expand composer"}
		>
			{fullscreen ? <ComposerCollapseIcon /> : <ComposerExpandIcon />}
		</button>
	) : null;

	// One stable DOM tree: the textarea is never remounted, so morphing pill↔card
	// preserves focus and in-progress input. Layout is driven by CSS grid areas.
	return (
		<div className={join(styles.inputWrapper, fullscreen && styles.wrapperFull)}>
			<div className={join(styles.composer, isCard ? styles.card : styles.pill, fullscreen && styles.full)}>
				{isCard && hasAttachments && (
					<AttachmentChips attachments={attachments} onRemove={composer?.onRemoveAttachment} />
				)}
				{textarea}
				{expandEl}
				{showControls && attachEl && (
					<div className={styles.attachArea}>
						{attachEl}
						{fileInput}
					</div>
				)}
				{showControls && clusterEl}
			</div>
			{disclaimerText !== null && <div className={styles.inputDisclaimer}>{disclaimerText}</div>}
		</div>
	);
};
